import {
  memo,
  useContext,
  useEffect,
  useMemo,
  MouseEvent,
  useState,
} from "react";
import {
  Box,
  Paper,
  Typography,
  useTheme,
  Tooltip,
  Theme,
  Stack,
} from "@mui/material";
import { Handle, Position } from "reactflow";
import IconTransform from "../iconsElement";
import { useStyles } from "./styles";
import { AccessTimeOutlined, TextSnippetOutlined } from "@mui/icons-material";
import { MapaContext } from "../../../contexts/mapaContext";
import { uptimeDoHost } from "../../../utils/easyMaps/uptimeItem";

export type ViewType = "compact" | "simple" | "detailed";

export interface ElementNodeData {
  hosts?: any[];
  entangled?: boolean;
  showLabel?: boolean;
  tipoMapa?: number;
  alerts?: any[];
  title?: string;
  editMode?: boolean;
  img?: string;
  easyMapsConfig?: any;
  configElement?: any;
  connectionMode?: string;
  viewType?: ViewType;
  descricao?: string;
  size?: number;
}

export interface ElementNodeProps {
  id: string;
  type?: string;
  severity?: number;
  sourcePosition?: Position;
  targetPosition?: Position;
  data: ElementNodeData;
  selected?: boolean;
}

interface ColorsState {
  background: string;
  border: string;
}

interface ViewProps {
  theme: Theme;
  colors: ColorsState;
  configElement: any;
  editMode?: boolean;
  img?: string;
  title?: string;
  hosts?: any[];
  descricao?: string;
  styles: any;
  scale?: number;
  uptime?: { valor: string; completo?: string; mapeado: boolean } | null;
}

const NODE_SIZE_MIN = 1;
const NODE_SIZE_MAX = 50;
const NODE_SCALE_MAX = 4;

export const computeNodeScale = (size?: number) => {
  if (!size || Number(size) <= 0) return 1;
  const clamped = Math.min(
    Math.max(Number(size), NODE_SIZE_MIN),
    NODE_SIZE_MAX,
  );
  return (
    1 +
    ((clamped - NODE_SIZE_MIN) * (NODE_SCALE_MAX - 1)) /
      (NODE_SIZE_MAX - NODE_SIZE_MIN)
  );
};

const resolveThemePath = (theme: any, path: string) => {
  return path?.split(".").reduce((obj: any, key: string) => obj?.[key], theme);
};

export const DetailedView: React.FC<ViewProps> = ({
  theme,
  colors,
  configElement,
  editMode,
  img,
  title,
  hosts,
  styles,
  descricao,
  scale = 1,
  uptime,
}) => (
  <Paper
    elevation={0}
    sx={{
      ...styles.detailedPaper(configElement, editMode, colors, scale),
      flexDirection: "column",
      alignItems: "stretch",
    }}
  >
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: `${8 * scale}px`,
      }}
    >
      <Box
        sx={{
          backgroundColor: colors.border || theme.palette.secondary.main,
          width: `${4 * scale}px`,
          height: `${36 * scale}px`,
          borderRadius: `${4 * scale}px`,
          transition: "width 0.18s ease, height 0.18s ease",
        }}
      />

      <Box sx={styles.iconBox(configElement, editMode, colors, false, scale)}>
        <IconTransform
          img={img}
          size={`${24 * scale}px`}
          severity={configElement?.severity}
          editMode={editMode}
        />
        <Box sx={styles.badge(configElement, editMode, scale)} />
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          gap: 0.5,
          flex: 1,
        }}
      >
        <Tooltip title={title || ""} arrow>
          <Typography noWrap sx={styles.titleText(scale)}>
            {title}
          </Typography>
        </Tooltip>
        <Stack
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography sx={styles.subTitleText(configElement, editMode, scale)}>
            {hosts?.[0]?.interfaces?.[0]?.ip}
          </Typography>
          {descricao && (
            <TextSnippetOutlined
              sx={{
                color: theme.palette.text.disabled,
                fontSize: `${0.75 * scale}rem`,
              }}
            />
          )}
        </Stack>
      </Box>
    </Box>

    {/* uptime do host: sempre por último no card detalhado */}
    {uptime && (
      <Tooltip
        title={
          uptime.mapeado
            ? `Uptime: ${uptime.completo ?? uptime.valor}`
            : "O host tem sysUpTime, mas não o item uptimeString (convertido)"
        }
        arrow
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: `${4 * scale}px`,
            pt: `${5 * scale}px`,
            borderTop: `1px solid ${theme.palette.divider}`,
            color: theme.palette.text.secondary,
            minWidth: 0,
          }}
        >
          <AccessTimeOutlined
            sx={{ fontSize: `${0.75 * scale}rem`, flexShrink: 0 }}
          />
          <Typography
            noWrap
            sx={{
              fontSize: `${0.65 * scale}rem`,
              lineHeight: 1.3,
              ...(uptime.mapeado
                ? { color: theme.palette.text.primary, fontWeight: 500 }
                : { fontStyle: "italic", color: theme.palette.text.disabled }),
            }}
          >
            {uptime.mapeado && (
              <Box
                component="span"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 400,
                  mr: 0.5,
                }}
              >
                Uptime
              </Box>
            )}
            {uptime.valor}
          </Typography>
        </Box>
      </Tooltip>
    )}
  </Paper>
);

export const SimpleView: React.FC<ViewProps> = ({
  theme,
  colors,
  configElement,
  editMode,
  img,
  title,
  styles,
  scale = 1,
}) => (
  <Paper
    elevation={0}
    sx={styles.simplePaper(configElement, editMode, colors, scale)}
  >
    <Box sx={styles.iconBox(configElement, editMode, colors, true, scale)}>
      <IconTransform
        img={img}
        size={`${20 * scale}px`}
        severity={configElement?.severity}
        editMode={editMode}
      />
      <Box
        sx={{
          ...styles.badge(configElement, editMode, scale),
          right: -2.5 * scale,
          bottom: -3 * scale,
          width: 12 * scale,
          height: 12 * scale,
        }}
      />
    </Box>

    <Tooltip title={title || ""} arrow>
      <Typography
        noWrap
        sx={{
          color: theme.palette.text.primary,
          fontSize: `${0.75 * scale}rem`,
          fontWeight: 600,
          lineHeight: 1.2,
          flex: 1,
        }}
      >
        {title}
      </Typography>
    </Tooltip>
  </Paper>
);

export const CompactView: React.FC<ViewProps> = ({
  colors,
  configElement,
  editMode,
  img,
  title,
  styles,
  scale = 1,
}) => (
  <Paper
    elevation={0}
    sx={styles.compactPaper(configElement, editMode, colors, scale)}
  >
    <Tooltip title={title || ""} arrow>
      <Box
        sx={{
          display: "flex",
          position: "relative",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <IconTransform
          img={img}
          size={`${34 * scale}px`}
          severity={configElement?.severity}
          editMode={editMode}
        />
      </Box>
    </Tooltip>
  </Paper>
);

const Element = ({ id, data, selected }: ElementNodeProps) => {
  const theme = useTheme();
  const isModeLight = theme.palette.mode === "light";
  const [colors, setColors] = useState<ColorsState>({
    background: (theme.palette as any).color?.zabbixSeverty?.default || "#fff",
    border:
      (theme.palette as any).color?.zabbixSeverty?.selectedTranslucid || "#ccc",
  });
  const [alertCount, setAlertCount] = useState(0);
  const [animationAlert, setAnimationAlert] = useState(true);
  const {
    hosts,
    entangled,
    alerts,
    title,
    editMode,
    img,
    easyMapsConfig,
    configElement,
    viewType,
    descricao,
    size,
  } = data;

  const scale = computeNodeScale(size);

  // itens ao vivo do host (coleta); o nó pode renderizar fora do provider (preview)
  const liveHosts = useContext(MapaContext)?.hostsDataMap?.get(id);
  const uptime = useMemo(() => uptimeDoHost(liveHosts), [liveHosts]);

  const styles = useStyles(theme, isModeLight);
  const viewTypeEl =
    viewType && viewType.trim() !== ""
      ? viewType
      : easyMapsConfig?.configuracoes?.viewType;

  useEffect(() => {
    if (!easyMapsConfig) return;
    setAnimationAlert(
      easyMapsConfig?.configuracoes?.animacao?.alertas !== false,
    );
  }, [easyMapsConfig]);

  useEffect(() => {
    if (!alerts) {
      setAlertCount(0);
      return;
    }
    setAlertCount(alerts.length);
  }, [alerts]);

  useEffect(() => {
    const config = configElement;
    if (editMode) {
      setColors({
        background:
          (theme.palette as any).color?.zabbixSeverty?.default || "#fff",
        border:
          (theme.palette as any).color?.zabbixSeverty?.selectedTranslucid ||
          "#ccc",
      });
      return;
    }
    if (!config) return;

    setColors({
      border: resolveThemePath(theme, config?.border) || "#ccc",
      background: resolveThemePath(theme, config?.background) || "#fff",
    });
  }, [configElement, editMode, theme]);

  const isCompact = viewTypeEl === "compact";
  const isSimple = viewTypeEl === "simple";
  const colorsOpacity = colors.border || "#a855f7";
  const hasAlert = configElement?.severity > 1 && animationAlert && !editMode;

  const viewProps: ViewProps = {
    theme,
    colors,
    configElement,
    editMode,
    img,
    title,
    hosts,
    styles,
    descricao,
    scale,
    uptime,
  };

  const renderView = () => {
    switch (viewTypeEl) {
      case "compact":
        return <CompactView {...viewProps} />;
      case "simple":
        return <SimpleView {...viewProps} />;
      case "detailed":
      default:
        return <DetailedView {...viewProps} />;
    }
  };

  return (
    <div
      style={{
        opacity: entangled ? 1 : 0.7,
        position: "relative",
      }}
      onDoubleClick={(event: MouseEvent<HTMLDivElement>) =>
        event.stopPropagation()
      }
    >
      {hasAlert && !selected && (
        <Box sx={styles.alertHalo(colors, viewTypeEl, selected, scale)} />
      )}

      {selected && (
        <Box
          sx={
            styles.bow(
              colorsOpacity,
              isCompact,
              isSimple,
              hasAlert,
              scale,
            ) as any
          }
        />
      )}

      {renderView()}

      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom"
        isConnectable={editMode}
        style={styles.handle(editMode, scale)}
      />
      <Handle
        type="source"
        position={Position.Top}
        id="top"
        isConnectable={editMode}
        style={styles.handle(editMode, scale)}
      />
      <Handle
        type="source"
        position={Position.Left}
        id="left"
        isConnectable={editMode}
        style={styles.handle(editMode, scale)}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="right"
        isConnectable={editMode}
        style={styles.handle(editMode, scale)}
      />
    </div>
  );
};

export default memo(Element);
